// src/resources/sections/hostConfig.ts
import type { ResourceConfig } from "../resourceTypes";
export const HOST_CONFIG: ResourceConfig[] = [
  {
    name: "hosts",
    label: "Hosts",
    schema: "host",
    promptUseSudo: true,
    defaultUseSudo: false,
    toolbarMode: "select",
    defaultSort: { field: "updated_at", order: "desc" },
    fields: [
      {
        source: "hostname",
        label: "Hostname",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "port",
        label: "Port",
        kind: "number",
        color: "gold",
        list: true,
        form: true,
        defaultValue: 22,
      },
      {
        source: "protocol",
        label: "Protocol",
        kind: "select",
        list: true,
        form: true,
        choices: [
          { value: "ssh", label: "ssh" },
          { value: "winrm", label: "winrm" },
        ],
        defaultValue: "ssh",
      },
      {
        source: "skip_cert_validation",
        label: "Skip TLS",
        kind: "boolean",
        list: true,
        form: true,
        defaultValue: false,
      },
      {
        source: "use_sudo",
        label: "Use Sudo",
        kind: "boolean",
        list: true,
        form: true,
        defaultValue: false,
      },
      {
        source: "os_release_id",
        label: "OS Release",
        kind: "select",
        form: true,
        list: false,
        editDisabled: true,
        ref: {
          resource: "os_releases",
          optionText: "name",
          schema: "host",
        },
      },
      {
        source: "os_release_name",
        label: "OS Release Name",
        kind: "text",
        form: true,
        list: true,
        displayOnly: true,
        valueFrom: "os_release_id",
        ref: {
          resource: "os_releases",
          optionText: "name",
          schema: "host",
        },
      },
      {
        source: "os_release_version_id",
        label: "OS Version ID",
        kind: "text",
        form: true,
        list: true,
        displayOnly: true,
        valueFrom: "os_release_id",
        ref: {
          resource: "os_releases",
          optionText: "version_id",
          schema: "host",
        },
      },

      {
        source: "os_family",
        label: "OS Family",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "prefer_ip",
        label: "Prefer IP",
        kind: "boolean",
        list: true,
        form: true,
      },
      {
        source: "shell_id",
        label: "shell_id",
        kind: "integer",
        form: true,
        list: true,
        ref: {
          resource: "shells",
          optionText: "code",
          input: "autocomplete",
          schema: "host",
        },
      },
      {
        source: "cred_id",
        label: "cred_id",
        kind: "uuid",
        form: true,
        list: true,
        ref: {
          resource: "credentials_safe",
          optionText: "cred_name",
          input: "autocomplete",
          schema: "auth",
        },
      },
      {
        source: "exec_mode",
        label: "exec_mode",
        kind: "select",
        list: true,
        form: true,
        choices: [
          { value: "inline", label: "inline" },
          { value: "file", label: "file" },
        ],
        defaultValue: "inline",
      },
      {
        source: "keytool_path",
        label: "Keytool path",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "owner",
        label: "File Owner",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "created_at",
        label: "created_at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
      {
        source: "updated_at",
        label: "updated_at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
    ],
    searchFields: ["hostname", "note"],
    enableSelection: true as any, // add to ResourceConfig type if you want strict typing
    actions: [
      {
        kind: "rpc",
        label: "Set keytool path",
        rpcName: "set_keytool_path",
        method: "POST",
        // sendRecord: true,
        sendRecord: false,
        useIdAs: "p_host_id",
        row: true,
        schema: "host",
        refreshAfter: true,
      },
      {
        kind: "rpc",
        label: "Test keytool",
        rpcName: "test_keytool",
        method: "POST",
        // sendRecord: true,
        sendRecord: false,
        useIdAs: "p_host_id",
        row: true,
        schema: "automation",
        refreshAfter: true,
      },

      {
        kind: "rpc",
        label: "Run Ad-hoc Command",
        rpcName: "run_hosts_command_batch",
        schema: "host",
        method: "POST",
        toolbar: true,
        // custom flag so GenericCrud knows this uses selected rows
        batchHosts: true,
        refreshAfter: false,
      },
      {
        kind: "rpc",
        label: "Run Discover OS Release for all Hosts",
        rpcName: "discover_os_release_for_hosts",
        schema: "host",
        method: "POST",
        toolbar: true,
        // custom flag so GenericCrud knows this uses selected rows
        // batchHosts: true,
        refreshAfter: true,
      },
      {
        kind: "rpc",
        label: "Run Command Group",
        rpcName: "run_command_group_for_hosts",
        method: "POST",
        toolbar: true,
        schema: "host",

        // NEW: tell GenericCrud to send selected row IDs as this arg
        // → p_host_ids will be uuid[] on the server side
        useSelectedIdsAs: "p_host_ids",

        // User picks which group to run
        promptArgs: [
          {
            kind: "select",
            name: "p_command_group_id",
            label: "Command Group",
            ref: {
              resource: "command_groups",
              schema: "host",
              optionText: "name",
            },
          },
        ],

        refreshAfter: false,
      },
    ],
  },
  {
    name: "command_groups",
    label: "Command Groups",
    schema: "host",
    fields: [
      {
        source: "name",
        label: "Name",
        kind: "text",
        list: true,
        form: true,
        required: true,
      },
      {
        source: "description",
        label: "Description",
        kind: "longtext",
        list: true,
        form: true,
      },
      {
        source: "enabled",
        label: "Enabled",
        kind: "boolean",
        list: true,
        form: true,
        defaultValue: true,
      },
      {
        source: "created_at",
        label: "Created At",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
      {
        source: "updated_at",
        label: "Updated At",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
    ],
    searchFields: ["name", "description"],
  },
  {
    name: "command_group_commands",
    label: "Command Group Commands",
    schema: "host",
    fields: [
      {
        source: "command_group_id",
        label: "Command Group",
        kind: "uuid",
        list: true,
        form: true,
        required: true,
        ref: {
          resource: "command_groups",
          schema: "host",
          optionText: "name",
          input: "select",
        },
      },
      {
        source: "command_id",
        label: "Command",
        kind: "uuid",
        list: true,
        form: true,
        required: true,
        ref: {
          resource: "commands",
          schema: "host",
          optionText: "name",
          input: "select",
        },
      },
      {
        source: "sort_order",
        label: "Order",
        kind: "number",
        list: true,
        form: true,
        required: true,
        defaultValue: 100,
      },
      {
        source: "enabled",
        label: "Enabled",
        kind: "boolean",
        list: true,
        form: true,
        required: true,
        defaultValue: true,
      },
      {
        source: "stop_on_error",
        label: "Stop on Error",
        kind: "boolean",
        list: true,
        form: true,
        required: true,
        defaultValue: true,
      },
      {
        source: "created_at",
        label: "Created At",
        kind: "date",
        list: true,
        form: false,
      },
      {
        source: "updated_at",
        label: "Updated At",
        kind: "date",
        list: true,
        form: false,
      },
    ],
    searchFields: ["command_group_id", "command_id"],
  },

  {
    name: "host_key_automations",
    label: "Host SSH Key Automations (Linux)",
    schema: "host",
    fields: [
      {
        source: "dest_alias",
        label: "dest_alias",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "host_id",
        label: "host_id",
        kind: "uuid",
        form: true,
        list: true,
        ref: {
          resource: "hosts",
          optionText: "hostname",
          input: "autocomplete",
        },
      },
      {
        source: "host_key_id",
        kind: "uuid",
        form: true,
        list: true,
        ref: {
          schema: "ssh",
          resource: "v_ssh_keys",
          // optionText: "identity",
          optionText: "name",
          input: "autocomplete",
        },
      },
      {
        source: "host_cert_id",
        label: "Host SSH Cert Subject Key ID",
        kind: "uuid",
        form: true,
        list: true,
        ref: {
          schema: "ssh",
          resource: "v_ssh_certificates",
          optionText: "key_id",
          // optionText: "identity",
          input: "autocomplete",
        },
      },
      {
        source: "cred_id",
        label: "cred_id",
        kind: "uuid",
        form: true,
        list: true,
        ref: {
          resource: "credentials_safe",
          optionText: "cred_name",
          input: "autocomplete",
          schema: "auth",
        },
      },
      {
        source: "enabled",
        label: "Enabled",
        kind: "boolean",
        list: true,
        form: true,
      },
      {
        source: "dest_path",
        label: "Dest Dir Path",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "file_mode_key",
        label: "File Mode Key",
        kind: "text",
        list: true,
        form: true,
        defaultValue: "600",
      },
      {
        source: "file_mode_pub",
        label: "File Mode Pub",
        kind: "text",
        list: true,
        form: true,
        defaultValue: "644",
      },
      {
        source: "file_mode_cert",
        label: "File Mode Cert",
        kind: "text",
        list: true,
        form: true,
        defaultValue: "644",
      },
      {
        source: "service_reload",
        label: "Service Reload",
        kind: "text",
        list: false,
        form: true,
        defaultValue:
          "systemctl reload-or-restart ssh.service || systemctl reload-or-restart ssh.socket || systemctl reload-or-restart sshd.service || systemctl reload-or-restart sshd.socket",
      },
      {
        source: "create_dirs",
        label: "Create Dirs",
        kind: "boolean",
        list: true,
        form: true,
      },
      {
        source: "backup_existing",
        label: "Backup Existing",
        kind: "boolean",
        list: true,
        form: true,
      },
      {
        source: "use_sudo",
        label: "Use Sudo",
        kind: "boolean",
        list: true,
        form: true,
      },
      {
        source: "last_run_at",
        label: "Last Run At",
        kind: "date",
        list: true,
        form: true,
      },
      {
        source: "last_result",
        label: "Last Result",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "last_error",
        label: "Last Error",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "updated_at",
        label: "Updated At",
        kind: "date",
        list: true,
        form: true,
      },
    ],
    searchFields: ["host_id"],
    actions: [
      {
        kind: "rpc",
        label: "Run Automation",
        rpcName: "run_host_key_automation",
        method: "POST",
        sendRecord: false,
        useIdAs: "p_automation_id",
        row: true,
        refreshAfter: true,
      },
      {
        kind: "rpc",
        label: "Run Command Batch",
        rpcName: "run_commands_for_automation",
        method: "POST",
        row: true,
        schema: "host",
        useIdAs: "p_automation_id",
        refreshAfter: true,
      },
    ],
  },
  /*
  {
    name: "host_keys",
    hideCreate: true,
    label: "Host Keys",
    schema: "host",
    fields: [
      {
        source: "name",
        label: "Name",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "hostname",
        label: "Hostname",
        kind: "text",
        list: true,
        form: true,
        editDisabled: true,
      },
      {
        source: "key_type",
        label: "key_type",
        kind: "text",
        list: true,
        form: false,
      },
      {
        source: "public_key",
        label: "public_key",
        kind: "text",
        list: true,
        form: true,
        editDisabled: true,
      },
      {
        source: "note",
        label: "Note",
        kind: "text",
        list: true,
        form: true,
      },
    ],
    searchFields: ["name", "hostname"],
    actions: [
      {
        kind: "rpc",
        label: "Create Host Key", // this will show in the toolbar
        schema: "host",
        rpcName: "create_host_key", // /rpc/create_host_key
        method: "POST",
        toolbar: true, // NOT row-level; we create new rows

        promptArgs: [
          {
            name: "p_hostname",
            label: "Hostname (e.g. web1.example.com)",
          },
          {
            name: "p_name",
            label: "Key Name (short label)",
          },
          {
            name: "p_key_type",
            label: "Key Type (ed25519, rsa2048, rsa3072, etc.)",
            defaultValue: "ed25519",
            optional: true, // so they can just accept default
          },
          {
            name: "p_note",
            label: "Note (optional)",
            defaultValue: "",
            optional: true,
          },
        ],

        // after creating a key/row reload table
        refreshAfter: true,
      },
    ],
  },
  {
    name: "host_certs",
    label: "Host Certs",
    schema: "host",
    hideCreate: true,
    fields: [
      {
        source: "identity",
        label: "identity",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "principals",
        label: "principals",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "valid_from",
        label: "Valid From",
        kind: "date",
        list: true,
        form: true,
      },
      {
        source: "valid_to",
        label: "Valid To",
        kind: "date",
        list: true,
        form: true,
      },
      {
        source: "cert_text",
        label: "Cert Text",
        kind: "longtext",
        list: true,
        form: true,
        scroll: { height: 200, width: 600 },
        editDisabled: true,
      },
      {
        source: "created_by",
        label: "Created By",
        kind: "uuid",
        list: true,
        form: true,
      },
    ],
    searchFields: ["identity"],
    actions: [
      {
        kind: "rpc",
        label: "Issue Host Cert",
        rpcName: "issue_host_cert",
        schema: "host", // or ssh, depending where the function lives
        method: "POST",
        toolbar: true, // NOT row-level; we create new rows
        useIdAs: "p_host_key_id", // row.id → p_host_key_id
        refreshAfter: true,
        promptArgs: [
          {
            kind: "select",
            name: "p_ca_id",
            label: "Certificate Authority",
            ref: {
              resource: "certificate_authorities_safe", // PostgREST resource
              schema: "ssh",
              optionText: "name",
            },
          },
          {
            kind: "select",
            name: "p_host_key_id",
            label: "Certificate Host Key",
            ref: {
              resource: "host_keys",
              schema: "host",
              optionText: "name",
            },
          },
          {
            kind: "text",
            name: "p_identity",
            label: "Identity",
            defaultValue: "",
          },
          {
            name: "p_principals",
            label: "Principals (comma separated)",
            optional: true,
            asTextArray: true,
          },
          {
            kind: "text",
            name: "p_valid_for",
            label: "Valid for (interval, e.g. '364 days')",
            defaultValue: "364 days",
            optional: true,
          },
        ],

        // after creating a key/row reload table
        // refreshAfter: true,
      },
    ],
  },
*/
  {
    name: "commands", // PostgREST path: /commands, schema: host
    label: "Host Commands",
    schema: "host",
    fields: [
      { source: "id", kind: "uuid", list: false, form: false },

      {
        source: "key",
        label: "Key",
        kind: "text",
        list: true,
        form: true,
        required: true,
      },
      {
        source: "name",
        label: "Name",
        kind: "text",
        list: true,
        form: true,
        required: true,
      },
      {
        source: "description",
        label: "Description",
        kind: "longtext",
        list: true,
        form: true,
      },
      {
        source: "command_template",
        label: "Command Template",
        kind: "longtext",
        list: true,
        form: true,
        required: true,
        autoScrollLargeValues: true, // uses your scrollable cell
      },
      {
        source: "exec_mode",
        label: "exec_mode",
        kind: "select",
        list: true,
        form: true,
        choices: [
          { value: "inline", label: "inline" },
          { value: "file", label: "file" },
        ],
        defaultValue: "inline",
      },
      {
        source: "timeout_seconds",
        label: "Timeout (seconds)",
        kind: "number",
        list: true,
        form: true,
      },

      // audit / metadata – read-only in UI
      {
        source: "created_at",
        label: "Created At",
        kind: "date",
        list: true,
        form: false,
      },
      {
        source: "updated_at",
        label: "Updated At",
        kind: "date",
        list: true,
        form: false,
      },
      {
        source: "owner_id",
        kind: "uuid",
        list: false,
        form: false,
      },
      {
        source: "created_by",
        kind: "uuid",
        list: false,
        form: false,
      },
      {
        source: "updated_by",
        kind: "uuid",
        list: false,
        form: false,
      },
    ],
    searchFields: ["name", "description", "command_template"],
  },

  {
    name: "command_runs",
    label: "Command Runs",
    schema: "host",

    //  hideCreate: true, // runs are created ONLY by automation, never manually
    // hideEdit: true, // read-only in UI
    readOnly: true,
    defaultSort: { field: "started_at", order: "desc" },

    fields: [
      { source: "id", kind: "uuid", list: false, form: false },

      // FK: automation_id → host_key_automations.id
      {
        source: "automation_id",
        label: "Automation",
        kind: "uuid",
        list: true,
        form: false,
        ref: {
          resource: "host_key_automations",
          schema: "host",
          optionText: "dest_alias",
          input: "select",
        },
      },
      {
        source: "host_id",
        label: "Hostname",
        kind: "uuid",
        list: true,
        form: false,
        ref: {
          resource: "hosts",
          optionText: "hostname",
          input: "select",
        },
      },

      // FK: command_id → host.commands.id
      {
        source: "command_id",
        label: "Command Name",
        kind: "uuid",
        list: true,
        form: false,
        ref: {
          resource: "commands",
          schema: "host",
          optionText: "name",
          input: "select",
        },
      },

      // FK: status_id → command_run_status.id
      {
        source: "status_id",
        label: "Status",
        kind: "number",
        list: true,
        form: false,
        ref: {
          resource: "command_run_status",
          schema: "host",
          optionText: "name", // your table has: id, name, description?
          input: "select",
        },
      },

      {
        source: "raw_command",
        label: "Raw Command",
        kind: "longtext",
        list: true,
        form: false,
        autoScrollLargeValues: true,
      },

      {
        source: "exit_code",
        label: "Exit Code",
        kind: "number",
        list: true,
        form: false,
      },

      // stdout often large – scrollable
      {
        source: "stdout",
        label: "Stdout",
        kind: "longtext",
        list: false, // too large for table
        form: false,
        scroll: { height: 200, width: 600 },
        autoScrollLargeValues: true,
      },

      // stderr often large – scrollable
      {
        source: "stderr",
        label: "Stderr",
        kind: "longtext",
        list: false,
        form: false,
        scroll: { height: 200, width: 600 },
        autoScrollLargeValues: true,
      },

      {
        source: "error_message",
        label: "Error Message",
        kind: "longtext",
        list: true,
        form: false,
        autoScrollLargeValues: true,
      },

      {
        source: "started_at",
        label: "Started At",
        kind: "date",
        list: true,
        form: false,
      },
      {
        source: "finished_at",
        label: "Finished At",
        kind: "date",
        list: true,
        form: false,
      },

      // audit fields
      {
        source: "created_at",
        label: "Created At",
        kind: "date",
        list: false,
        form: false,
      },
      {
        source: "updated_at",
        label: "Updated At",
        kind: "date",
        list: false,
        form: false,
      },
      { source: "created_by", kind: "uuid", list: false, form: false },
      { source: "updated_by", kind: "uuid", list: false, form: false },
      { source: "owner_id", kind: "uuid", list: false, form: false },
    ],

    searchFields: ["raw_command", "error_message"],

    // No actions – runs are read-only records
    // actions: [],
    actions: [
      {
        kind: "viewOutput",
        label: "View Output",
        row: true, // shows up in the row's ellipsis menu
      },
    ],
  },

  {
    name: "host_key_automation_commands", // /host_key_automation_commands, schema: host
    label: "Host Key Automation Commands",
    schema: "host",
    fields: [
      { source: "id", kind: "uuid", list: false, form: false },

      {
        source: "automation_id",
        label: "Automation",
        kind: "uuid",
        list: true,
        form: true,
        required: true,
        ref: {
          resource: "host_key_automations", // you already have this resource/table
          optionText: "dest_alias", // or whatever label field you prefer
          schema: "host",
          input: "select",
        },
      },
      {
        source: "command_id",
        label: "Command",
        kind: "uuid",
        list: true,
        form: true,
        required: true,
        ref: {
          resource: "commands",
          optionText: "name",
          schema: "host",
          input: "select",
        },
      },
      {
        source: "sort_order",
        label: "Order",
        kind: "number",
        list: true,
        form: true,
        required: true,
        defaultValue: 100,
      },
      {
        source: "enabled",
        label: "Enabled",
        kind: "boolean",
        list: true,
        form: true,
        required: true,
        defaultValue: true,
      },
      {
        source: "stop_on_error",
        label: "Stop on Error",
        kind: "boolean",
        list: true,
        form: true,
        required: true,
        defaultValue: true,
      },

      // audit fields
      {
        source: "created_at",
        label: "Created At",
        kind: "date",
        list: true,
        form: false,
      },
      {
        source: "updated_at",
        label: "Updated At",
        kind: "date",
        list: true,
        form: false,
      },
      {
        source: "owner_id",
        kind: "uuid",
        list: false,
        form: false,
      },
      {
        source: "created_by",
        kind: "uuid",
        list: false,
        form: false,
      },
      {
        source: "updated_by",
        kind: "uuid",
        list: false,
        form: false,
      },
    ],
    // searching by IDs isn’t amazing, but still useful
    searchFields: ["id", "command_id"],
  },
  {
    name: "os_discovery_debug", // /host_key_automation_commands, schema: host
    label: "os_discovery_debug",
    schema: "host",
    readOnly: true,
    defaultSort: { field: "created_at", order: "desc" },
    fields: [
      {
        source: "created_at",
        kind: "date",
        list: true,
        form: true,
      },
      {
        source: "host_id",
        label: "Hostname",
        kind: "text",
        form: true,
        list: true,
        editDisabled: true,
        ref: {
          resource: "hosts",
          optionText: "hostname",
          schema: "host",
        },
      },
      {
        source: "hostname",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "step",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "message",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "detail",
        kind: "text",
        list: true,
        form: true,
      },
    ],
  },
];
